import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "@/lib/utils";
import { type AsChildProps, resolveRenderProp } from "./render-prop";

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90",
        outline:
          "border-white/10 bg-white/[0.04] text-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] [a&]:hover:bg-accent/10",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Badge({
  asChild = false,
  children,
  className,
  render,
  variant,
  ...props
}: useRender.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> &
  AsChildProps) {
  const renderProps = resolveRenderProp({ asChild, children, render });

  return useRender({
    defaultTagName: "span",
    render: renderProps.render,
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ variant }), className),
        "data-slot": "badge",
      } as React.ComponentProps<"span">,
      { ...props, children: renderProps.children },
    ),
  });
}

export { Badge, badgeVariants };
